import React, { useState, useRef } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import listPlugin from "@fullcalendar/list";
import interactionPlugin from "@fullcalendar/interaction";
import { get } from "../../services/api";
import html2pdf from "html2pdf.js";

const colors = [
  "#98D8C8",
  "#4ECDC4",
  "#45B7D1",
  "#FFA07A",
  "#C7B198",
  "#F7DC6F",
  "#FF6B6B",
];
const stringToColor = (str) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++)
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
};

function BookedAssessments({
  heading = "Booked Assessments",
  onBack,
  eventSources,
}) {
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const pdfRef = useRef();
  const calendarRef = useRef();
  const [calendarReady, setCalendarReady] = useState(false);
  const token = localStorage.getItem("token");

  const defaultEventSources = eventSources || [
    {
      fetchUrl: async (fromDate, toDate) => {
        const response = await get(
          `CommonSettings/GetAllScheduleLogByFromDateAndToDateAsync?fromDate=${fromDate}&toDate=${toDate}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        return response; // Adjust this based on the expected response handling
      },
      type: heading === "Booked Assessments" ? "assessment" : "daily_activity",
    },
  ];

  const handleEventClick = (clickInfo) => {
    const { event } = clickInfo;
    setSelectedEvent({
      title: event.extendedProps.title || "No title",
      timeSlot: event.extendedProps.slotName,
      date: event.start.toISOString().split("T")[0],
      description: event.extendedProps.description || "No description",
      teachers: event.extendedProps.teachers || "No teachers",
      eventType: event.extendedProps.eventType || "Unknown",
    });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedEvent(null);
  };

  const fetchEvents = (fetchInfo, successCallback) => {
    const fromDate = fetchInfo.startStr.split("T")[0];
    const toDate = fetchInfo.endStr.split("T")[0];
    Promise.all(
      defaultEventSources.map((source) =>
        get(source.fetchUrl(fromDate, toDate)).then((response) =>
          response.isSuccess
            ? response.scheduleLog
                .filter((log) => log.scheduleDate && log.slotName && log.title)
                .map((log) => {
                  const date = log.scheduleDate.split("T")[0];
                  const [startTime, endTime] = log.slotName
                    .split(" ")
                    .map((t) => t.trim() || (t ? "09:00" : "09:30"));
                  const start = `${date}T${startTime}:00`;
                  const end =
                    endTime === startTime
                      ? new Date(new Date(start).getTime() + 30 * 60000)
                          .toISOString()
                          .slice(0, 19)
                      : `${date}T${endTime}:00`;
                  return {
                    id: log.id,
                    title: `${log.title} - ${log.slotName}`,
                    start,
                    end,
                    allDay: false,
                    backgroundColor: stringToColor(log.title),
                    borderColor: stringToColor(log.title),
                    extendedProps: {
                      description: log.remarks,
                      teachers: log.teachersName,
                      eventType: source.type,
                      title: log.title,
                      slotName: log.slotName,
                    },
                  };
                })
            : []
        )
      )
    ).then((allEvents) => {
      successCallback(allEvents.flat());
      setCalendarReady(true);
    });
  };

  const handleDatesRender = () => {
    setCalendarReady(false);
    if (calendarRef.current) calendarRef.current.getApi().refetchEvents();
  };

  const generatePdf = () => {
    if (!calendarReady) {
      alert("Please wait for the calendar to load.");
      return;
    }
    if (calendarRef.current) calendarRef.current.getApi().render();
    const element = pdfRef.current;
    const options = {
      margin: 0.5,
      filename: "student-performance-report.pdf",
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: {
        scale: 1,
        useCORS: true,
        width: 800,
        height: element.scrollHeight,
      },
      jsPDF: { unit: "in", format: [11.69, 8.27], orientation: "landscape" },
    };
    html2pdf().set(options).from(element).save();
  };

  return (
    <div className="booked-assessments">
      {onBack && (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: "1rem",
          }}
        >
          <h1>{heading}</h1>
          <div>
            <button
              className="btn btn-primary me-2"
              onClick={generatePdf}
              title="PDF"
            >
              PDF
            </button>
            <button
              className="btn btn-primary"
              onClick={onBack}
              title="Back to Dashboard"
            >
              Back
            </button>
          </div>
        </div>
      )}
      {!onBack && <h1>{heading}</h1>}
      <div ref={pdfRef} style={{ width: "800px", overflow: "visible" }}>
        <FullCalendar
          ref={calendarRef}
          plugins={[
            dayGridPlugin,
            timeGridPlugin,
            listPlugin,
            interactionPlugin,
          ]}
          initialView="dayGridMonth"
          events={fetchEvents}
          eventClick={handleEventClick}
          dayMaxEvents={true}
          headerToolbar={{
            left: "prev,next today",
            center: "title",
            right: "dayGridMonth,timeGridWeek,timeGridDay,listWeek",
          }}
          height="auto"
          contentHeight="auto"
          slotMinTime="08:00:00"
          slotMaxTime="20:00:00"
          scrollTime="09:00:00"
          slotEventOverlap={false}
          datesRender={handleDatesRender}
        />
      </div>
      {showModal && selectedEvent && (
        <>
          <div
            className="modal fade show"
            tabIndex="-1"
            style={{ display: "block" }}
          >
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5>Event Details</h5>
                  <button
                    className="btn-close"
                    onClick={handleCloseModal}
                  ></button>
                </div>
                <div className="modal-body">
                  <dl className="row">
                    <dt className="col-sm-3 fw-bold">Event Type</dt>
                    <dd className="col-sm-9">{selectedEvent.eventType}</dd>
                    <dt className="col-sm-3 fw-bold">Title</dt>
                    <dd className="col-sm-9">{selectedEvent.title}</dd>
                    <dt className="col-sm-3 fw-bold">Time Slot</dt>
                    <dd className="col-sm-9">{selectedEvent.timeSlot}</dd>
                    <dt className="col-sm-3 fw-bold">Date</dt>
                    <dd className="col-sm-9">{selectedEvent.date}</dd>
                    <dt className="col-sm-3 fw-bold">Description</dt>
                    <dd className="col-sm-9">{selectedEvent.description}</dd>
                    <dt className="col-sm-3 fw-bold">Teachers</dt>
                    <dd className="col-sm-9">{selectedEvent.teachers}</dd>
                  </dl>
                </div>
                <div className="modal-footer">
                  <button
                    className="btn btn-primary"
                    onClick={handleCloseModal}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </>
      )}
    </div>
  );
}

export default BookedAssessments;
